import i18n, { loadLocale } from '../i18n/i18n';
import { captureGenericError } from '../sentry';

/**
 * Creates and displays a native login UI overlay with options for LINE, Apple, and guest login.
 *
 * The overlay includes a customizable background image, styled buttons for each login method,
 * and a loading spinner. The UI is appended to the document body and can be cleaned up when no longer needed.
 *
 * @returns An object containing references to the overlay container, each login button,
 * a loading state handler, and a cleanup function:
 * - `container`: The root HTMLDivElement of the overlay.
 * - `loginButton`: The LINE login HTMLButtonElement.
 * - `appleButton`: The Apple login HTMLButtonElement.
 * - `guestButton`: The guest login HTMLButtonElement.
 * - `loading(enabled: boolean)`: Shows the spinner and hides buttons when `enabled` is true, and vice versa.
 * - `cleanup()`: Removes the overlay from the DOM.
 *
 * @example
 * ```typescript
 * const ui = nativeLoginUI();
 * ui.loginButton.onclick = () => { ... };
 * ui.loading(true);
 * // ...
 * ui.cleanup();
 * ```
 */
export async function nativeLoginUI() {
  // Load the locale for the user language.
  await loadUserLocale();

  const container = document.createElement('div');

  container.id = 'buruburu-login-overlay';

  container.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-image: image-set(
        ${
          process.env.IS_LOCAL_DEVELOPMENT
            ? ''
            : `
        url('splash/splash_test_c.avif') type('image/avif'),
        url('splash/splash_test_c.webp') type('image/webp'),`
        }
        url('splash/splash_test_c.png') type('image/png')
      );
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      font-family: sans-serif;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
      z-index: 9999;
    ">
      <style>
        #buruburu-login-overlay .button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 300px;
          padding: 14px;
          margin: 10px;
          border: none;
          border-radius: 12px;
          font-size: 18px;
          font-weight: bold;
          cursor: pointer;
          transition: 0.2s;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          user-select: none;
          background-clip: padding-box;
        }

        #buruburu-login-overlay .no-focus-outline .button:focus {
          outline: none;
        }

        #buruburu-login-overlay .button:active {
          transform: scale(0.96);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        #buruburu-login-overlay .line-button {
          background-color: #3ACE01;
          padding: 6px;
          color: white;
        }

        #buruburu-login-overlay .apple-button {
          background-color: white;
          color: black;
          border: 1px solid #ccc;
        }

        #buruburu-login-overlay .guest-button {
          background-color: #333;
          color: white;
        }

        #buruburu-login-overlay .icon {
          width: 24px;
          height: 24px;
          margin-right: 12px;
        }

        #buruburu-login-overlay .line-button .icon {
          width: 48px;
          height: 48px;
        }

        #buruburu-login-overlay .buttons {
          margin-bottom: 40px;
        }

        #buruburu-login-overlay .spinner {
          display: none;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 50px;
          height: 50px;
          border: 8px solid rgba(255, 255, 255, 0.3); /* Increased thickness */
          border-top: 8px solid white; /* Increased thickness */
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }
      </style>
      <div class="spinner"></div>
      <div class="buttons">
        <button class="button line-button">
          <svg aria-hidden="true" class="icon" viewBox="0 0 377.764 377.764" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" fill="#3ACE01" d="M77.315 0h223.133c42.523 0 77.315 34.792 77.315 77.315v223.133c0 42.523-34.792 77.315-77.315 77.315H77.315C34.792 377.764 0 342.972 0 300.448V77.315C0 34.792 34.792 0 77.315 0z"></path><path fill-rule="evenodd" clip-rule="evenodd" fill="#FFF" d="M188.515 62.576c76.543 0 138.593 49.687 138.593 110.979 0 21.409-7.576 41.398-20.691 58.351-.649.965-1.497 2.031-2.566 3.209l-.081.088c-4.48 5.36-9.525 10.392-15.072 15.037-38.326 35.425-101.41 77.601-109.736 71.094-7.238-5.656 11.921-33.321-10.183-37.925-1.542-.177-3.08-.367-4.605-.583l-.029-.002v-.002c-64.921-9.223-114.222-54.634-114.222-109.267-.002-61.292 62.049-110.979 138.592-110.979z"></path><path fill-rule="evenodd" clip-rule="evenodd" fill="#3ACE01" d="M108.103 208.954h27.952c3.976 0 7.228-3.253 7.228-7.229v-.603c0-3.976-3.252-7.228-7.228-7.228h-20.121v-45.779c0-3.976-3.252-7.228-7.228-7.228h-.603c-3.976 0-7.228 3.252-7.228 7.228v53.609c0 3.977 3.252 7.23 7.228 7.23zm173.205-33.603v-.603c0-3.976-3.253-7.228-7.229-7.228h-20.12v-11.445h20.12c3.976 0 7.229-3.252 7.229-7.228v-.603c0-3.976-3.253-7.228-7.229-7.228h-27.952c-3.976 0-7.228 3.252-7.228 7.228v53.609c0 3.976 3.252 7.229 7.228 7.229h27.952c3.976 0 7.229-3.253 7.229-7.229v-.603c0-3.976-3.253-7.228-7.229-7.228h-20.12v-11.445h20.12c3.976.002 7.229-3.251 7.229-7.226zm-53.755 31.448l.002-.003a7.207 7.207 0 0 0 2.09-5.07v-53.609c0-3.976-3.252-7.228-7.229-7.228h-.603c-3.976 0-7.228 3.252-7.228 7.228v31.469l-26.126-35.042c-1.248-2.179-3.598-3.655-6.276-3.655h-.603c-3.976 0-7.229 3.252-7.229 7.228v53.609c0 3.976 3.252 7.229 7.229 7.229h.603c3.976 0 7.228-3.253 7.228-7.229v-32.058l26.314 35.941c.162.252.339.494.53.724l.001.002c.723.986 1.712 1.662 2.814 2.075.847.35 1.773.544 2.742.544h.603a7.162 7.162 0 0 0 3.377-.844c.723-.344 1.332-.788 1.761-1.311zm-71.208 2.155h.603c3.976 0 7.228-3.253 7.228-7.229v-53.609c0-3.976-3.252-7.228-7.228-7.228h-.603c-3.976 0-7.229 3.252-7.229 7.228v53.609c0 3.976 3.253 7.229 7.229 7.229z"></path>
          </svg>
          ${i18n('native.login.line')}
        </button>

        <button class="button apple-button">
          <svg aria-hidden="true" class="icon" xmlns="http://www.w3.org/2000/svg" xml:space="preserve" viewBox="0 0 814 1000">
            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
          </svg>
          ${i18n('native.login.apple')}
        </button>

        <button class="button guest-button">
          ${i18n('native.login.guest')}
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(container);

  const spinner = container.querySelector('.spinner') as HTMLDivElement;
  const buttons = container.querySelector('.buttons') as HTMLDivElement;

  const loading = (enabled: boolean) => {
    if (enabled) {
      spinner.style.display = 'block';
      buttons.style.display = 'none';
    } else {
      spinner.style.display = 'none';
      buttons.style.display = 'block';
    }
  };

  // Function to clean up UI
  const cleanup = () => {
    if (container && container.parentNode) {
      container.remove();
    }
  };

  return {
    container,
    loginButton: container.querySelector('.line-button') as HTMLButtonElement,
    appleButton: container.querySelector('.apple-button') as HTMLButtonElement,
    guestButton: container.querySelector('.guest-button') as HTMLButtonElement,
    loading,
    cleanup,
  };
}

/**
 * Load the locale for the user language.
 */
async function loadUserLocale() {
  try {
    const userLanguage = navigator.language || navigator.languages[0] || 'ja';
    const languageToLoad = userLanguage.startsWith('en') ? 'en' : 'ja';
    await loadLocale(languageToLoad);
  } catch (e) {
    captureError('Error loading user locale:', e);
  }
}

/**
 * Wrapper for capturing errors, that will fallback to console.error if Sentry is not available.
 * @param message
 * @param error
 */
function captureError(message: string, error: unknown) {
  try {
    captureGenericError(message, error);
  } catch {
    console.error(message, error);
  }
}
